import type { CreateAdminSocietyDto, UpdateAdminSocietyDto } from '@idc-forms/api-client'
import type {
  V2CreateAdminSocietyInput,
  V2UpdateAdminSocietyInput
} from '@idc-forms/schemas/api-v2/admin-societies'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import useAuth from '@/features/auth/hooks/use-auth'
import { queryKeys } from '@/shared/constants/query-keys'
import { getErrorMessage } from '@/shared/utils/handle-api-error'
import { societiesAdminV2Service } from '../services/societies-admin-v2.service'

/**
 * Mutaciones de sociedades contra la API v2. Identifican por id público (`soc_`)
 * y, al completar, invalidan todo el árbol `['societies']` (listado + summary +
 * detalle). El editor de página completa no existe: el alta/edición vive en el
 * side panel.
 */
function useV2AccessToken() {
  const { auth } = useAuth()
  return auth?.v2AccessToken ?? ''
}

export const useCreateSocietyV2Mutation = () => {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()
  return useMutation({
    // Se acepta el tipo de DOMINIO (schema Zod) y se castea en el borde del SDK:
    // el generado y el schema describen el mismo dato en runtime pero TS los ve
    // como tipos distintos.
    mutationFn: async (body: V2CreateAdminSocietyInput) =>
      await societiesAdminV2Service.create(accessToken, body as CreateAdminSocietyDto),
    onSuccess: () => {
      toast.success('Sociedad creada')
      void queryClient.invalidateQueries({ queryKey: queryKeys.societies.all })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })
}

export const useUpdateSocietyV2Mutation = () => {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: V2UpdateAdminSocietyInput }) =>
      await societiesAdminV2Service.update(accessToken, id, body as UpdateAdminSocietyDto),
    onSuccess: () => {
      toast.success('Sociedad actualizada')
      void queryClient.invalidateQueries({ queryKey: queryKeys.societies.all })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })
}

export const useDeleteSocietyV2Mutation = () => {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()
  return useMutation({
    mutationFn: async (id: string) => await societiesAdminV2Service.remove(accessToken, id),
    onSuccess: () => {
      toast.success('Sociedad eliminada')
      void queryClient.invalidateQueries({ queryKey: queryKeys.societies.all })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })
}
